/* src/Utils/exportLogsToExcel.ts */
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Log } from '../Modules/Log/Model/Log';
import { Account } from '../Modules/Account/Model/Account';
import dayjs from 'dayjs';
import { extractChangedFieldsFromLogDescription } from './logChangedFields';

/** 기본 엑셀 헤더 순서 (변경 필드 컬럼 제외) */
const BASE_HEADERS = [
  'logId', 'action', 'sellerId', 'sellerParentId', 'slotId', 'start_date', 'end_date', 
  'total_days', 'final_days', 'created_at'
] as const;

const fieldLabel: Record<string, string> = {
  main_keyword: '메인 키워드',
  word_keyword: '작업 키워드',
  mid_single: '단일 MID',
  mid_compare: '가격비교 MID',
  product_url: '상품 URL',
  compare_url: '가격비교 URL',
  memo: '메모',
  start_date: '시작일',
  end_date: '종료일',
  status: '상태',
  user: '아이디',
  manager: '생성자',
};

function valueToText(v: any): string {
  if (v === null || v === undefined) return '-';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

/**
 * 모든 로그에서 변경된 필드 목록 수집 (정렬된 순서)
 */
function collectAllChangedFields(logs: Log[]): string[] {
  const fieldSet = new Set<string>();
  logs.forEach(log => {
    const changed = extractChangedFieldsFromLogDescription(
      log.description,
      log.slot_snapshot ?? undefined,
    );
    Object.keys(changed).forEach(k => fieldSet.add(k));
  });
  return Array.from(fieldSet).sort();
}

/**
 * 로그 → 엑셀(.xlsx) 내보내기
 * @param logs     현재 화면(필터링)에서 내려 보낼 로그 배열
 * @param accounts 전체 계정 리스트 (id ➜ 이름 매핑용)
 * @param activeFilter 현재 활성화된 필터 ('today' | 'yesterday' | 'this_month' | 'custom' | null)
 * @param range 사용자 지정 기간 범위
 */
export function exportLogsToExcel(
  logs: Log[], 
  accounts: Account[], 
  activeFilter?: 'today' | 'yesterday' | 'this_month' | 'custom' | null,
  range?: [any | null, any | null] | null
) {
  /* ▸ 1. id→이름 사전 */
  const nameMap = new Map<number, string>(
    accounts.map(a => [a.id, `${a.username} (${a.name})`]),
  );

  /* ▸ 2. 변경된 필드 목록 수집 */
  const changedFields = collectAllChangedFields(logs);
  
  /* ▸ 3. 동적 헤더 생성: 기본 헤더 + 각 필드별 (before, after) */
  const headers: string[] = [...BASE_HEADERS];
  const headerLabels: string[] = [
    '로그 ID', '행위', '아이디', '상위 계정 아이디', '슬롯 ID', '시작일', '종료일',
    '기간', '발주일수', '일시'
  ];
  
  // 각 변경 필드에 대해 before 먼저, after 다음으로 컬럼 추가
  changedFields.forEach(fieldKey => {
    const fieldName = fieldLabel[fieldKey] || fieldKey;
    headers.push(`${fieldKey}_before`, `${fieldKey}_after`);
    headerLabels.push(`${fieldName} (before)`, `${fieldName} (after)`);
  });

  /* ▸ 4. 행(plain object) 변환 */
  const rows = logs.map(log => {
    const row: Record<string, any> = {};
    const changed = extractChangedFieldsFromLogDescription(
      log.description,
      log.slot_snapshot ?? undefined,
    );

    // 기본 헤더 처리
    BASE_HEADERS.forEach(k => {
      switch (k) {
        case 'sellerId':
          row.sellerId = log.sellerUsername
            ?? (log.sellerId ? (nameMap.get(log.sellerId) ?? log.sellerId) : '');
          break;
        case 'sellerParentId':
          row.sellerParentId = log.sellerParentUsername
            ?? (log.sellerParentId ? (nameMap.get(log.sellerParentId) ?? log.sellerParentId) : '');
          break;
        case 'start_date':
          row.start_date = log.start_date ? dayjs(log.start_date).format('YYYY-MM-DD') : '';
          break;
        case 'end_date':
          row.end_date = log.end_date ? dayjs(log.end_date).format('YYYY-MM-DD') : '';
          break;
        case 'created_at':
          row.created_at = dayjs(log.created_at).format('YYYY-MM-DD HH:mm:ss');
          break;
        default:
          row[k] = (log as any)[k] ?? '';
      }
    });

    // 변경 필드별 before/after 값 채우기
    changedFields.forEach(fieldKey => {
      const fieldData = changed[fieldKey];
      row[`${fieldKey}_before`] = fieldData ? valueToText(fieldData.before) : '';
      row[`${fieldKey}_after`] = fieldData ? valueToText(fieldData.after) : '';
    });

    return row;
  });

  /* ▸ 5. 워크시트/워크북 생성 */
  const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
  
  // 한글 헤더로 변경
  XLSX.utils.sheet_add_aoa(ws, [headerLabels], { origin: 'A1' });

  // 컬럼 너비 설정
  const colWidths: Array<{ wch: number }> = [
    { wch: 10 }, // 로그 ID
    { wch: 14 }, // 행위
    { wch: 24 }, // 아이디
    { wch: 24 }, // 상위 계정 아이디
    { wch: 10 }, // 슬롯 ID
    { wch: 12 }, // 시작일
    { wch: 12 }, // 종료일
    { wch: 8 },  // 기간
    { wch: 10 }, // 발주일수
    { wch: 20 }, // 일시
  ];
  
  // 변경 필드 컬럼 너비 (before/after 각각)
  changedFields.forEach(() => {
    colWidths.push({ wch: 30 }, { wch: 30 }); // before, after 각각 30
  });
  
  (ws as any)['!cols'] = colWidths;
  
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'logs');

  /* ▸ 6. 파일명 생성 */
  const generateFilename = () => {
    const today = dayjs();
    
    switch (activeFilter) {
      case 'today':
        const todayFormat = today.format('M.D');
        return `스티젠 로그 ${todayFormat}~${todayFormat}.xlsx`;
        
      case 'yesterday':
        const yesterday = today.subtract(1, 'day');
        const yesterdayFormat = yesterday.format('M.D');
        return `스티젠 로그 ${yesterdayFormat}~${yesterdayFormat}.xlsx`;
        
      case 'this_month':
        const monthStart = today.startOf('month').format('M.D');
        const monthEnd = today.endOf('month').format('M.D');
        return `스티젠 로그 ${monthStart}~${monthEnd}.xlsx`;
        
      case 'custom':
        if (range && (range[0] || range[1])) {
          const startDate = range[0] ? dayjs(range[0]).format('M.D') : '';
          const endDate = range[1] ? dayjs(range[1]).format('M.D') : '';

          if (startDate && endDate) {
            return `스티젠 로그 ${startDate}~${endDate}.xlsx`;
          }
          if (startDate) {
            return `스티젠 로그 ${startDate}부터.xlsx`;
          }
          if (endDate) {
            return `스티젠 로그 ${endDate}까지.xlsx`;
          }
        }
        break;
    }
    
    // 기본값 (오늘 날짜)
    const todayFormat = today.format('M.D');
    return `스티젠 로그 ${todayFormat}~${todayFormat}.xlsx`;
  };

  /* ▸ 7. 다운로드 */
  const arrayBuf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const filename = generateFilename();
  saveAs(new Blob([arrayBuf], { type: 'application/octet-stream' }), filename);
} 